import {
  ChoiceGroup,
  DatePicker,
  DefaultButton,
  Dropdown,
  IDropdownOption,
  MaskedTextField,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
  Toggle,
  TooltipHost,
} from '@fluentui/react';
import { Patient } from '../../api';
import * as _ from 'lodash';
import * as React from 'react';
import { GENDER_TYPE, PATIENT_LANGUAGES, PATIENT_TYPE } from '../../config/constants';
import { CreatePatientRequest, EditPatientRequest } from '../../redux/patient/types';
import { Payer } from '../../redux/visits/types';
import styles from './PatientForm.module.scss';

function createNewPatient(patient?: Patient): CreatePatientRequest {
  return {
    firstName: patient ? patient.firstName : '',
    lastName: patient ? patient.lastName : '',
    phoneNumber: patient ? patient.phone : '',
    email: patient ? patient.email : '',
    preferredLanguage: patient?.preferredLanguage || 'en',
    relationship: patient ? patient.relationship : '',
    hasMedicareMedicaid: patient ? patient.hasMedicareMedicaid : false,
    hasMedicaid: patient ? patient.hasMedicaid : false,
    dateOfBirth: patient && patient.dateOfBirth ? new Date(patient.dateOfBirth) : undefined,
    gender: patient ? patient.gender || '' : '',
    avatarUrl: patient ? patient.avatarUrl || '' : '',
  };
}

export interface PatientFormProps {
  isLoading?: boolean;
  patient?: Patient;
  accountOwner?: Patient;
  payers: Payer[];
  onSave: (patientId: string, data: CreatePatientRequest | EditPatientRequest) => void;
}

export interface PatientFormState {
  isEdit: boolean;
  patient: CreatePatientRequest;
  isMainPatient: boolean;
  maskedTextFieldKey: number;
}

export class PatientForm extends React.Component<PatientFormProps, PatientFormState> {
  constructor(props: PatientFormProps) {
    super(props);

    this.state = {
      maskedTextFieldKey: 0,
      isEdit: !!props.patient,
      patient: createNewPatient(props.patient),
      isMainPatient: !!props.patient?.mainPatientOfAccount,
    };
  }

  public componentDidMount() {
    const currentPatient = this.props.patient;
    if (currentPatient) {
      this.setPatientData(currentPatient);
    }
  }

  public componentDidUpdate(prevProps: PatientFormProps) {
    if (this.props.patient && prevProps.patient !== this.props.patient) {
      this.setPatientData(this.props.patient);
    }
  }

  public render(): JSX.Element {
    if (this.props.isLoading) {
      return <Spinner size={SpinnerSize.large} style={{ padding: 20 }} label="Saving patient information..." ariaLive="assertive" />;
    }

    return (
      <div className={styles.container}>
        {this.renderForm()}
        <PrimaryButton style={{ marginTop: 20 }} onClick={this.onSaveClick} disabled={!this.isFormValid() || this.props.isLoading} text={this.state.isEdit ? 'Save' : 'Add'} />
      </div>
    );
  }

  private renderForm(): JSX.Element {
    return (
      <Stack tokens={{ childrenGap: 6 }}>
        {!this.state.isEdit && <DefaultButton style={{ display: 'block', marginBottom: 10 }} text="Copy from account owner" onClick={this.copyInfosFromAccountOwner} />}
        <TextField
          name="firstName"
          value={this.state.patient.firstName}
          onChange={(e, value) => this.setState({ patient: { ...this.state.patient, firstName: _.trimStart(value) } })}
          label="First Name"
          required
        />

        <TextField
          name="lastName"
          value={this.state.patient.lastName}
          onChange={(e, value) => this.setState({ patient: { ...this.state.patient, lastName: _.trimStart(value) } })}
          label="Last Name"
          required
        />

        {this.buildEmailTextField()}

        <MaskedTextField
          key={this.state.maskedTextFieldKey}
          name="phone"
          value={this.state.patient.phoneNumber}
          onChange={(e, value) => this.setState({ patient: { ...this.state.patient, phoneNumber: _.trim(value) } })}
          label="Phone Number"
          required
          mask="+9 (999)-999-9999"
          maskChar=""
          maxLength={17}
        />

        <Dropdown
          onChange={this.onRelationshipChange}
          options={PATIENT_TYPE.map((m) => ({ key: m.text, text: m.text }))}
          disabled={false}
          required
          selectedKey={this.state.patient.relationship}
          label="Relationship"
        />

        <ChoiceGroup
          className="inline-flex"
          defaultSelectedKey={this.state.patient.gender}
          options={GENDER_TYPE.map((m) => ({ key: m.text, text: m.text }))}
          onChange={this.onGenderRadioChange}
          label="Sex"
          required
        />

        <ChoiceGroup
          className="inline-flex"
          defaultSelectedKey={this.state.patient.preferredLanguage ?? PATIENT_LANGUAGES.en}
          onChange={this.onPreferredLanguageChange}
          options={Object.entries(PATIENT_LANGUAGES).map(([key, value]) => ({ key, text: value }))}
          label="Preferred Language"
          required
        />

        <DatePicker
          onSelectDate={this.onBirthdayChange}
          label="Date of Birth (m/d/yyyy)"
          value={this.state.patient.dateOfBirth ? new Date(this.state.patient.dateOfBirth) : undefined}
          isRequired={true}
          allowTextInput={true}
          disableAutoFocus={true}
          formatDate={this.onFormatDate}
        />

        <Toggle
          defaultChecked={this.state.patient.hasMedicaid}
          label="Enrolled in Medicaid"
          onText="Yes"
          offText="No"
          onChange={(ev, checked) => this.setState({ patient: { ...this.state.patient, hasMedicaid: checked! } })}
        />
      </Stack>
    );
  }

  private buildEmailTextField(): JSX.Element {
    const content = (
      <TextField
        name="email"
        value={this.state.patient.email}
        onChange={(e, value) => this.setState({ patient: { ...this.state.patient, email: _.trim(value) } })}
        disabled={this.state.isMainPatient}
        label="E-mail"
        required
      />
    );

    if (this.state.isMainPatient) {
      return <TooltipHost content="This field is view only. Contact your supervisor in order to edit username in Admin Tools">{content}</TooltipHost>;
    } else {
      return content;
    }
  }

  private copyInfosFromAccountOwner = () => {
    const { accountOwner } = this.props;
    if (accountOwner) {
      this.setState({
        maskedTextFieldKey: this.state.maskedTextFieldKey + 1,
        patient: {
          ...this.state.patient,
          phoneNumber: accountOwner.phone,
          email: accountOwner.email,
        },
      });
    }
  };

  private onFormatDate = (date?: Date): string => {
    return date ? date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear() : '';
  };

  private isFormValid(): boolean {
    const { patient } = this.state;

    const isValid =
      !!patient.relationship &&
      !!patient.gender &&
      !!patient.firstName &&
      !!patient.lastName &&
      !!patient.phoneNumber &&
      !!patient.email &&
      !!patient.dateOfBirth &&
      patient.dateOfBirth instanceof Date;

    if (!isValid) {
      return false;
    }

    return isValid;
  }

  private onBirthdayChange = (dateOfBirth: any) => {
    if (dateOfBirth instanceof Date) {
      this.setState({
        patient: {
          ...this.state.patient,
          dateOfBirth,
        },
      });
    }
  };

  private onRelationshipChange = (e: React.FormEvent, item?: IDropdownOption) => {
    if (item) {
      this.setState({
        patient: {
          ...this.state.patient,
          relationship: item.text,
        },
      });
    }
  };

  private onPreferredLanguageChange = (e?: React.FormEvent, option?: { text: string; key: string }) => {
    if (option) {
      this.setState({
        patient: {
          ...this.state.patient,
          preferredLanguage: option.key.toString(),
        },
      });
    }
  };

  private onGenderRadioChange = (e?: React.FormEvent, option?: { text: string; key: string }) => {
    this.setState({
      patient: {
        ...this.state.patient,
        gender: option!.text.toUpperCase(),
      },
    });
  };

  private onSaveClick = () => {
    const data = { ...this.state.patient };
    data.gender = data.gender.toUpperCase();
    this.props.onSave(this.props.patient ? this.props.patient.patientId : '', data);
  };

  private setPatientData(patient: Patient) {
    this.setState({ patient: createNewPatient(patient) });
  }
}
